/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useCareAi } from './useCareAi';
import { DeclineScoreForm, DeclineScoreResult } from './DeclineScoreParts';
import { FamilyUpdateForm, FamilyUpdateResult } from './FamilyUpdateParts';

/**
 * Pannello AI del Team di cura (Fase 23):
 *  - Punteggio di declino clinico (equipe) — NEWS2 / Braden / Barthel + vitali + note
 *  - Aggiornamento per i familiari (equipe genera, paziente/familiari leggono)
 */
export const CareAiPanel = ({ teamId, isPatient }) => {
  const canGenerate = !isPatient;
  const { loading, busy, runDecline, runFamily, latest } = useCareAi(teamId, { canGenerate });
  const decline = latest('decline_score');
  const family = latest('family_update');

  return (
    <section className="space-y-3" data-testid="care-ai-panel">
      <h2 className="font-display font-semibold text-lg flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" /> Assistente AI del team
      </h2>
      <Card>
        <CardContent className="p-4">
          {loading ? <Skeleton className="h-24" /> : (
            <Tabs defaultValue={canGenerate ? 'decline' : 'family'}>
              <TabsList className="mb-3">
                {canGenerate && <TabsTrigger value="decline" data-testid="care-ai-tab-decline">Rischio di declino</TabsTrigger>}
                <TabsTrigger value="family" data-testid="care-ai-tab-family">Aggiornamento familiari</TabsTrigger>
              </TabsList>
              {canGenerate && (
                <TabsContent value="decline" className="space-y-4">
                  <DeclineScoreForm busy={busy === 'decline'} onRun={runDecline} />
                  <DeclineScoreResult result={decline?.result} createdAt={decline?.created_at} />
                </TabsContent>
              )}
              <TabsContent value="family" className="space-y-4">
                {canGenerate ? (
                  <FamilyUpdateForm busy={busy === 'family'} onRun={runFamily} />
                ) : (
                  <p className="text-sm text-muted-foreground">Qui trovi l'aggiornamento quotidiano preparato dall'equipe per te e i tuoi familiari.</p>
                )}
                {family
                  ? <FamilyUpdateResult result={family.result} createdAt={family.created_at} />
                  : <p className="text-xs text-muted-foreground" data-testid="care-ai-family-empty">Nessun aggiornamento ancora generato.</p>}
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>
    </section>
  );
};
